<template>
  <AppHeader />
  <ArticleWrapper :badges="badges" :article="article" :pdfs="pdfs" :audios="audios" :videos="videos" />
  <LazyPodcasts v-if="Object.keys(podcasts).length > 1" class="pt-12 lg:pt-32 pb-20 lg:pb-44" />
  <LazyAppFooter :class="[Object.keys(podcasts).length > 1 ? 'pt-10' : 'pt-12 lg:pt-32']" />
</template>

<script setup lang="ts">
import { ArticleCategory, type ArticleContent, type Seo } from '~/utils/types'

const { gqlRootNames, gqlChunks, gqlProps, gqlMeta, gqlHash, clearGqlFields } = useGlobalGql()
const adminFetch = useAdminFetch()
const parishConfig = useParishConfig()
const podcasts = usePodcasts()
const route = useRoute()
const seo = useGlobalSeo()
const fixUrl = useFixUrl()
const category = computed<string | ArticleCategory>(() => (route.params?.category as string | ArticleCategory | undefined) || '')
const categories = useArticleCategories()
const slug = computed<string>(() => [...route.params.slug].filter(Boolean).join('/'))
const fullUrlArr = computed<string[]>(() => [category.value, slug.value].filter(Boolean))

const { getGqlPage } = useUtils()
const articles = useArticles()
const article = useArticle()
const { badges, pdfs, audios, videos } = useArticleContent()
const isArticlesPage = computed<boolean>(() => categories.value.map((i: any) => i.url).includes(category.value as ArticleCategory))

const existingArticle = computed<any>(() => articles.value?.find((article: any) => article.url === slug.value))
if (existingArticle.value && !route.query?.refresh) article.value = existingArticle.value

if (!fullUrlArr.value.includes(article.value?.url)) {
  gqlRootNames.push('articles', 'pages')
  gqlChunks.push(
    ['basic', 'description', 'publishDate', 'author', 'gallery', 'attachments', 'seo'],
    ['basic', 'description', 'gallery', 'attachments', 'seo'],
  )
  gqlProps.push(
    `filters: { url: { eq: "${slug.value}" }, category: { url: { eq: "${category.value}" } } }`,
    `filters: { url: { eq: "${fullUrlArr.value.join('/')}" } }`,
  )
  gqlMeta.push(false)
  gqlHash.push(fullUrlArr.value.join('/'))

  try {
    const { pages: gqlPages = {}, articles: gqlArticles = {} } = await adminFetch(gqlRootNames, gqlChunks, gqlProps, gqlMeta, gqlHash)
    const content = (await Promise.any([getGqlPage(gqlArticles), getGqlPage(gqlPages)])) as ArticleContent

    if (!content.isPodcastEpisode && isArticlesPage.value) {
      const { data } = await useFetch(() => '/api/read-time', { body: { data: [content] }, method: 'POST' })
      article.value = data.value?.data[0] || content
    } else {
      article.value = content
    }
  } catch (e: any) {
    if (e.message === 'All promises were rejected') {
      showError({ statusCode: 404, message: 'Požadovaná stránka nebola nájdená.' })
    } else {
      showError(e)
    }
  }

  clearGqlFields()
}

watch(
  () => isArticlesPage.value,
  async (newValue) => {
    provide('isBigHeading', !newValue)
  },
  { immediate: true },
)

useSeoMeta({
  ...(seo.value as Seo),
  ...(article.value.seo as Seo | null),
  ...(isArticlesPage.value && {
    ogType: 'article',
    author: article.value.author?.fullName || undefined,
    articleAuthor: article.value.author?.fullName || undefined,
    articlePublishedTime: article.value.publishedAt || undefined,
    articleModifiedTime: article.value.updatedAt || undefined,
    ...(article.value.image && {
      ogImageUrl: fixUrl(article.value.image.url),
      ogImageType: article.value.image.mime || undefined,
      twitterImage: fixUrl(article.value.image.url),
    }),
  }),
  ...(!article.value.seo && {
    title: `${article.value.heading} | ${parishConfig.value?.name}`,
    ogTitle: `${article.value.heading} | ${parishConfig.value?.name}`,
    twitterTitle: `${article.value.heading} | ${parishConfig.value?.name}`,
  }),
})
</script>
